import axios, { AxiosError, AxiosResponse } from 'axios';
import * as Sentry from '@sentry/react';

import { AwsTokenState } from '../../security/model/aws-token-state';
import { authService } from '../../security/services/authorization-service';
import { GuestToken, Tokens } from '../../security/services/SSO';

let isSSOState: boolean = false;
let useTokens: Tokens | GuestToken;
let isPayNowState: boolean = false;

let responseInterceptorInstalled = false;

// Statuses we don't want to forward to Sentry (expected/auth/validation).
const IGNORED_STATUSES = new Set<number>([401, 403, 404, 409, 422]);

// URL fragments that should never be reported (avoid feedback loops/noise).
const IGNORED_URL_FRAGMENTS = ['ingest.sentry.io', 'ingest.us.sentry.io'];

function shouldReport(error: AxiosError): boolean {
  const url = (error.config?.url ?? '') + ' ' + (error.config?.baseURL ?? '');
  if (IGNORED_URL_FRAGMENTS.some(f => url.includes(f))) return false;

  if (!error.response) return true;
  const status = error.response.status;
  if (status >= 500) return true;
  if (IGNORED_STATUSES.has(status)) return false;
  return status >= 400;
}

function installResponseInterceptor() {
  if (responseInterceptorInstalled) return;
  responseInterceptorInstalled = true;

  axios.interceptors.response.use(
    (response: AxiosResponse) => response,
    (error: AxiosError) => {
      try {
        if (shouldReport(error)) {
          const status = error.response?.status ?? 'network';
          const method = (error.config?.method ?? 'GET').toUpperCase();
          const url = error.config?.url ?? 'unknown';

          Sentry.captureException(error, scope => {
            scope.setTag('http.status', String(status));
            scope.setTag('http.method', method);
            scope.setTag('http.url', url);
            scope.setContext('http', {
              status,
              method,
              url,
              baseURL: error.config?.baseURL,
              code: error.code,
              message: error.message,
            });
            return scope;
          });
        }
      } catch {
        // Never let Sentry reporting break the app.
      }
      return Promise.reject(error);
    },
  );
}

export const HttpInterceptor = (
  isSSO?: boolean,
  tokens?: Tokens | GuestToken,
  isPayNow?: boolean,
) => {
  if (typeof isSSO === 'boolean') {
    isSSOState = isSSO;
  }

  if (typeof tokens !== 'undefined') {
    useTokens = tokens;
  }

  if (typeof isPayNow === 'boolean') {
    isPayNowState = isPayNow;
  }

  let id_token: string;

  axios.interceptors.request.use(
    async config => {
      if (!isSSOState && !isPayNowState) {
        const securityToken: AwsTokenState = await authService.fetchSecurityToken();
        if (!securityToken?.jwtIdToken)
          throw new Error(
            'There was an issue retrieving your user session. Your session may have timed out. Please log out and log back in to continue. If the problem persists, contact support for assistance.',
          );
        id_token = securityToken.jwtIdToken;
      } else if (!isSSOState && isPayNowState) {
        if ('token' in useTokens) {
          id_token = useTokens.token;
        }
      } else {
        if ('id_token' in useTokens) {
          id_token = useTokens.id_token;
        }
        if (typeof useTokens === 'undefined') {
          console.error('Token is required');
        }
      }

      config.headers['Authorization'] = `Bearer ${id_token}`;
      config.headers['x-cog-id'] = id_token;
      return config;
    },
    async err => {
      return Promise.reject(err);
    },
  );

  installResponseInterceptor();
};

export default HttpInterceptor;
